import React from 'react';
import { Box, Typography, CircularProgress } from '@mui/material';
import type { PendingTileState } from '../types';

interface Props {
  pending: PendingTileState;
  onClose: () => void;
}

const STAGES = ['uploading', 'submitting', 'analysing'] as const;
type Stage = typeof STAGES[number];

const STAGE_LABELS: Record<Stage, string> = {
  uploading: 'Uploading videos',
  submitting: 'Submitting job',
  analysing: 'AI analysis',
};

function formatEta(seconds: number): string {
  if (seconds < 60) return `${seconds}s left`;
  if (seconds < 3600) return `${Math.ceil(seconds / 60)}m left`;
  return `${Math.ceil(seconds / 3600)}h left`;
}

function formatSpeed(mbps: number): string {
  if (mbps < 1) return `${Math.round(mbps * 1000)} Kbps`;
  return `${mbps.toFixed(1)} Mbps`;
}

const PendingAnalysisModal: React.FC<Props> = ({ pending, onClose }) => {
  React.useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  const stageIdx = STAGES.indexOf(pending.stage as Stage);

  return (
    <Box
      onClick={onClose}
      sx={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9998, padding: 2 }}
    >
      <Box
        onClick={(e) => e.stopPropagation()}
        sx={{ backgroundColor: 'white', borderRadius: '28px', width: '100%', maxWidth: '540px', padding: '32px', boxShadow: '0 32px 80px rgba(0,0,0,0.4)' }}
      >
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
          <Typography sx={{ fontFamily: 'MuseoModerno', fontWeight: 600, fontSize: '20px', background: 'linear-gradient(to right, #F39A81, #959BEA)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
            {pending.petName ? `Analysing ${pending.petName}…` : 'Analysing…'}
          </Typography>
          <Box onClick={onClose} sx={{ width: 36, height: 36, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', backgroundColor: '#f5f5f5', '&:hover': { backgroundColor: '#e0e0e0' } }}>
            <Typography sx={{ fontSize: '20px', lineHeight: 1, color: '#555' }}>×</Typography>
          </Box>
        </Box>

        {/* Stage steps */}
        <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 3 }}>
          {STAGES.map((s, i) => (
            <React.Fragment key={s}>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.75, flex: '0 0 auto', minWidth: 72 }}>
                <Box sx={{
                  width: 32, height: 32, borderRadius: '50%',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  background: stageIdx >= i || pending.stage === 'failed'
                    ? 'linear-gradient(135deg, #F39A81, #959BEA)'
                    : '#eee',
                  transition: 'background 0.3s',
                }}>
                  {stageIdx > i && (
                    <Typography sx={{ color: 'white', fontSize: '15px', lineHeight: 1 }}>✓</Typography>
                  )}
                  {stageIdx === i && pending.stage !== 'failed' && (
                    <CircularProgress size={16} thickness={3} sx={{ color: 'white' }} />
                  )}
                  {(stageIdx < i || pending.stage === 'failed') && (
                    <Typography sx={{ color: stageIdx >= i ? 'white' : '#bbb', fontSize: '13px', lineHeight: 1, fontFamily: 'Maven Pro' }}>{i + 1}</Typography>
                  )}
                </Box>
                <Typography sx={{ fontFamily: 'Maven Pro', fontSize: '11px', color: stageIdx >= i ? '#555' : '#bbb', textAlign: 'center', lineHeight: 1.2 }}>
                  {STAGE_LABELS[s]}
                </Typography>
              </Box>
              {i < STAGES.length - 1 && (
                <Box sx={{ flex: 1, height: '2px', mt: '15px', background: stageIdx > i ? 'linear-gradient(to right, #F39A81, #959BEA)' : '#eee', transition: 'background 0.3s' }} />
              )}
            </React.Fragment>
          ))}
        </Box>

        {/* Per-file upload progress */}
        {pending.stage === 'uploading' && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.75 }}>
            {[
              { 
                label: pending.contentFileName, 
                pct: pending.contentProgress,
                eta: pending.contentEta,
                speed: pending.contentSpeed,
              },
              ...pending.resultFileNames.map((name, i) => ({ 
                label: name, 
                pct: pending.resultProgresses[i] ?? 0,
                eta: pending.resultEtas?.[i],
                speed: pending.resultSpeeds?.[i],
              })),
            ].map(({ label, pct, eta, speed }) => (
              <Box key={label}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography sx={{ fontFamily: 'Maven Pro', fontSize: '13px', color: '#555', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '60%' }}>
                    {label}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexShrink: 0, ml: 1 }}>
                    {pct < 100 && eta != null && eta > 0 && (
                      <Typography sx={{ fontFamily: 'Maven Pro', fontSize: '11px', color: '#999' }}>
                        {formatEta(eta)}
                      </Typography>
                    )}
                    {pct < 100 && speed != null && speed > 0 && (
                      <Typography sx={{ fontFamily: 'Maven Pro', fontSize: '11px', color: '#bbb' }}>
                        {formatSpeed(speed)}
                      </Typography>
                    )}
                    <Typography sx={{ fontFamily: 'Maven Pro', fontSize: '13px', color: '#959BEA', fontWeight: 700 }}>
                      {pct}%
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ height: '6px', backgroundColor: '#f0f0f0', borderRadius: '3px', overflow: 'hidden' }}>
                  <Box sx={{ height: '100%', width: `${pct}%`, background: 'linear-gradient(to right, #F39A81, #959BEA)', borderRadius: '3px', transition: 'width 0.3s ease' }} />
                </Box>
              </Box>
            ))}
          </Box>
        )}

        {pending.stage === 'submitting' && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 1 }}>
            <CircularProgress size={20} sx={{ color: '#959BEA' }} />
            <Typography sx={{ fontFamily: 'Maven Pro', fontSize: '14px', color: '#555' }}>Submitting analysis job…</Typography>
          </Box>
        )}

        {pending.stage === 'analysing' && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 1 }}>
            <CircularProgress size={20} sx={{ color: '#959BEA' }} />
            <Typography sx={{ fontFamily: 'Maven Pro', fontSize: '14px', color: '#555' }}>AI is analysing pet reactions — this may take a minute</Typography>
          </Box>
        )}

        {pending.stage === 'failed' && (
          <Box sx={{ backgroundColor: '#fff5f5', borderRadius: '12px', padding: 2, border: '1px solid #fed7d7' }}>
            <Typography sx={{ fontFamily: 'Maven Pro', fontSize: '14px', color: '#e53e3e' }}>
              {pending.error ?? 'Analysis failed. Please try again.'}
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default PendingAnalysisModal;
